<script lang="ts" setup>
    import { ref, watch, computed, onMounted, nextTick } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import EmojiInsert from '@shared/components/EmojiInsert.vue';
    import { getContactPropertyAutoComplete } from '@cwd/Views/Segments/services/requests';
    import { api } from '@cwd/lib/main';
    import { type SegmentProperty, type DynamicSubjectRow, NULLARY_OPERATORS, OPERATOR_RESOURCE_KEYS, deserialize, serialize } from './dynamicSubject';

    const OPERATORS = Object.entries(OPERATOR_RESOURCE_KEYS).map(([value, key]) => ({ value, label: $Resources[key] }));

    const props = defineProps<{
        issue: KnockoutObservable<{
            DynamicSubject: KnockoutObservable<string | null>;
            Subject: KnockoutObservable<string>;
        }>;
        onUpdateIsValid: (val: boolean) => void;
        onUpdate: (val: string | null) => void;
    }>();

    const storedMustache = ref<string | null>(null);
    const segmentPropertiesRaw = ref<SegmentProperty[]>([]);

    const filteredSegmentProperties = computed<{ value: string; label: string }[]>(() =>
        segmentPropertiesRaw.value.filter((p) => !/Created|IsActive|HasBounced/i.test(p.Name)).map((p) => ({ value: 'Contact.' + p.Name, label: p.Label }))
    );

    onMounted(async () => {
        segmentPropertiesRaw.value = (await api.get('/Segments/Properties')) as SegmentProperty[];

        props.issue.subscribe((newVal) => {
            if (newVal && segmentPropertiesRaw.value.length > 0) {
                init(newVal.DynamicSubject());
            }
        });

        if (props.issue()) {
            init(props.issue().DynamicSubject());
        }
    });

    const initialized = ref(false);

    const init = (mustache: string | null) => {
        initialized.value = false;

        if (!mustache) {
            rows.value = [{ property: filteredSegmentProperties.value[0]?.value ?? '', operator: 'eq', value: '', subject: '' }];
            defaultSubject.value = props.issue()?.Subject() ?? '';
            initialized.value = true;
            props.onUpdateIsValid(false);
            return;
        }

        const { rows: initialRows, defaultSubject: initialDefault } = deserialize(mustache);
        rows.value =
            initialRows.length > 0 ? initialRows : [{ property: filteredSegmentProperties.value[0]?.value ?? '', operator: 'eq', value: '', subject: '' }];
        defaultSubject.value = initialDefault || (props.issue()?.Subject() ?? '');

        initialized.value = true;
        props.onUpdateIsValid(allInputsFilled());
    };

    function isNullaryOp(op: string): boolean {
        return NULLARY_OPERATORS.has(op);
    }

    function getPropertyId(propertyString: string): number | null {
        const name = propertyString.replace(/^Contact\./, '');
        return segmentPropertiesRaw.value.find((p) => p.Name === name)?.Value ?? null;
    }

    const rows = ref<DynamicSubjectRow[]>([]);
    const defaultSubject = ref('');
    const hasInteracted = ref(false);

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    function allInputsFilled(): boolean {
        if (!defaultSubject.value.trim()) return false;
        return rows.value.every((row) => row.subject.trim() !== '' && (isNullaryOp(row.operator) || row.value.trim() !== ''));
    }

    const hasError = computed(() => {
        const hasError = initialized.value && !allInputsFilled();
        props.onUpdateIsValid(!hasError);

        return hasError;
    });

    function emitUpdate() {
        hasInteracted.value = true;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (!allInputsFilled()) return;
            props.onUpdate(serialize(rows.value, defaultSubject.value));
        }, 600);
    }

    watch([rows, defaultSubject], emitUpdate, { deep: true });

    function addRow() {
        const prevProperty = rows.value[rows.value.length - 1]?.property ?? filteredSegmentProperties.value[0]?.value ?? '';
        rows.value.push({ property: prevProperty, operator: 'eq', value: '', subject: '' });
    }

    function removeRow(index: number) {
        rows.value.splice(index, 1);
    }

    const baseId = 'dynSub';

    const rowInputRefs = ref<(HTMLInputElement | null)[]>([]);
    const defaultSubjectInputRef = ref<HTMLInputElement | null>(null);
    const rowCursors = ref<number[]>([]);
    const defaultCursor = ref(0);

    function setRowInputRef(el: HTMLInputElement | null, i: number) {
        rowInputRefs.value[i] = el;
    }

    function updateRowCursor(i: number) {
        rowCursors.value[i] = rowInputRefs.value[i]?.selectionStart ?? rows.value[i]?.subject.length ?? 0;
    }

    function updateDefaultCursor() {
        defaultCursor.value = defaultSubjectInputRef.value?.selectionStart ?? defaultSubject.value.length;
    }

    function insertMustacheInRow(field: string, i: number) {
        const tag = `{{${field}}}`;
        const current = rows.value[i].subject;
        const cursor = rowCursors.value[i] ?? current.length;
        rows.value[i].subject = current.slice(0, cursor) + tag + current.slice(cursor);
        const newCursor = cursor + tag.length;
        rowCursors.value[i] = newCursor;
        nextTick(() => {
            const input = rowInputRefs.value[i];
            if (input) {
                input.focus();
                input.setSelectionRange(newCursor, newCursor);
            }
        });
    }

    function insertEmojiInRow(emoji: string, i: number) {
        const current = rows.value[i].subject;
        const cursor = rowCursors.value[i] ?? current.length;
        rows.value[i].subject = current.slice(0, cursor) + emoji + current.slice(cursor);
        const newCursor = cursor + emoji.length;
        rowCursors.value[i] = newCursor;
        nextTick(() => {
            const input = rowInputRefs.value[i];
            if (input) {
                input.focus();
                input.setSelectionRange(newCursor, newCursor);
            }
        });
    }

    function insertEmojiInDefault(emoji: string) {
        const cursor = defaultCursor.value ?? defaultSubject.value.length;
        defaultSubject.value = defaultSubject.value.slice(0, cursor) + emoji + defaultSubject.value.slice(cursor);
        const newCursor = cursor + emoji.length;
        defaultCursor.value = newCursor;
        nextTick(() => {
            const input = defaultSubjectInputRef.value;
            if (input) {
                input.focus();
                input.setSelectionRange(newCursor, newCursor);
            }
        });
    }

    function insertMustacheInDefault(field: string) {
        const tag = `{{${field}}}`;
        const cursor = defaultCursor.value ?? defaultSubject.value.length;
        defaultSubject.value = defaultSubject.value.slice(0, cursor) + tag + defaultSubject.value.slice(cursor);
        const newCursor = cursor + tag.length;
        defaultCursor.value = newCursor;
        nextTick(() => {
            const input = defaultSubjectInputRef.value;
            if (input) {
                input.focus();
                input.setSelectionRange(newCursor, newCursor);
            }
        });
    }

    const switchBackToStaticSubject = () => {
        storedMustache.value = serialize(rows.value, defaultSubject.value);
        props.onUpdate(null);
        init(null);
    };

    const undo = () => {
        if (storedMustache.value) {
            init(storedMustache.value);
            props.onUpdate(storedMustache.value);
            storedMustache.value = null;
        }
    };
</script>

<template v-if="initialized">
    <h3 class="h4 m-lg-t">{{ $Resources.DynamicSubjectHeading }}</h3>

    <div class="d-flex gap-lg flex-direction-col m-lg-b">
        <div class="dynamic-subject-grid d-grid align-items-end gap-sm">
            <template v-for="(row, i) in rows" :key="i">
                <span class="align-with-input fw-600 align-self-start">{{ i === 0 ? $Resources.IfLabel : $Resources.ElseIfLabel }}:</span>

                <div class="d-flex flex-wrap gap-sm align-items-end">
                    <div class="condition-col-property">
                        <label :for="baseId + 'Prop' + i">{{ $Resources.PropertyLabel }}:</label>
                        <select :id="baseId + 'Prop' + i" v-model="row.property" class="form-control">
                            <option v-for="p in filteredSegmentProperties" :key="p.value" :value="p.value">{{ p.label }}</option>
                        </select>
                    </div>

                    <div class="condition-col-operator">
                        <label :for="baseId + 'Op' + i">{{ $Resources.FilterOperatorsLabel }}:</label>
                        <select :id="baseId + 'Op' + i" v-model="row.operator" class="form-control">
                            <option v-for="op in OPERATORS" :key="op.value" :value="op.value">{{ op.label }}</option>
                        </select>
                    </div>

                    <div class="condition-col-value" v-if="!isNullaryOp(row.operator)">
                        <SingleSelect
                            :model-value="row.value"
                            :useAutocomplete="true"
                            :allow-custom-item="true"
                            :fetchItems="(val: string) => getContactPropertyAutoComplete(getPropertyId(row.property), val)"
                            @update:modelValue="(val) => (row.value = val || '')"
                            :items="[]"
                            :label="$Resources.ValueLabel"
                            :placeholder="$Resources.ValueLabel + '...'" />
                    </div>

                    <div class="condition-col-subject p-relative">
                        <div class="d-flex gap-xs align-items-end">
                            <label class="m-auto-r" :for="baseId + 'Sub' + i">{{ $Resources.SubjectLabel }}:</label>
                            <div class="d-flex gap-xs p-absolute" style="right: 0">
                                <EmojiInsert btnClass="btn-link btn-sm p-xs-r p-xs-l" @select="insertEmojiInRow($event, i)" />
                                <BSDropdownButton
                                    :closeOnClickInside="true"
                                    btnClass="btn-link btn-sm p-xs-r p-xs-l"
                                    menuClass="dropdown-menu-right"
                                    maxHeight="300px">
                                    <template #toggle>{{ $Resources.InsertSnippetBtn }}</template>
                                    <template #content>
                                        <li v-for="p in filteredSegmentProperties" :key="p.value">
                                            <a href="javascript:void(0)" role="button" @click="insertMustacheInRow(p.value, i)">{{ p.label }}</a>
                                        </li>
                                    </template>
                                </BSDropdownButton>
                            </div>
                        </div>
                        <input
                            :id="baseId + 'Sub' + i"
                            v-model="row.subject"
                            type="text"
                            class="form-control"
                            :placeholder="$Resources.SubjectLabel"
                            :ref="(el) => setRowInputRef(el as HTMLInputElement | null, i)"
                            @blur="updateRowCursor(i)"
                            @click="updateRowCursor(i)"
                            @keyup="updateRowCursor(i)" />
                    </div>
                </div>

                <button type="button" class="btn btn-default" :disabled="rows.length === 1" @click="removeRow(i)" v-tooltip="$Resources.RemoveButton">
                    <span class="glyphicon glyphicon-trash" aria-hidden="true"></span>
                    <span class="sr-only">{{ $Resources.RemoveButton }}</span>
                </button>
            </template>

            <span class="align-with-input">
                <label :for="baseId + 'defaultSubject'" class="text-md m-0-b">{{ $Resources.ElseLabel }}:</label>
            </span>
            <div>
                <div class="d-flex gap-xs align-items-end justify-content-end">
                    <EmojiInsert btnClass="btn-link btn-sm p-xs-r p-xs-l" @select="insertEmojiInDefault($event)" />
                    <BSDropdownButton :closeOnClickInside="true" btnClass="btn-link btn-sm p-xs-r p-xs-l" menuClass="dropdown-menu-right" maxHeight="300px">
                        <template #toggle>{{ $Resources.InsertSnippetBtn }}</template>
                        <template #content>
                            <li v-for="p in filteredSegmentProperties" :key="p.value">
                                <a href="javascript:void(0)" role="button" @click="insertMustacheInDefault(p.value)">{{ p.label }}</a>
                            </li>
                        </template>
                    </BSDropdownButton>
                </div>
                <input
                    v-model="defaultSubject"
                    type="text"
                    class="form-control"
                    :id="baseId + 'defaultSubject'"
                    :placeholder="$Resources.SubjectLabel"
                    :ref="(el) => (defaultSubjectInputRef = el as HTMLInputElement | null)"
                    @blur="updateDefaultCursor"
                    @click="updateDefaultCursor"
                    @keyup="updateDefaultCursor" />
            </div>
        </div>

        <BsAlert
            class="m-0-b"
            :show="hasError"
            :type="AlertTypes.Danger"
            :dismissible="false"
            :title="$Resources.DynamicSubjectErrorTitle"
            :content="$Resources.DynamicSubjectErrorContent" />

        <div class="d-flex gap-sm justify-content-end">
            <button type="button" class="btn btn-subtle" @click="switchBackToStaticSubject">{{ $Resources.SwitchToStaticSubject }}</button>

            <button type="button" class="btn btn-default" v-show="storedMustache" @click="undo">{{ $Resources.RestorePreviousLabel }}</button>

            <button type="button" class="btn btn-default" :disabled="rows.length === 15" @click="addRow">
                <span class="glyphicon glyphicon-plus" aria-hidden="true"></span>
                {{ $Resources.AddConditionLabel }}
            </button>
        </div>
    </div>
</template>

<style>
    .dynamic-subject-grid {
        --_label-height: 24px;
        grid-template-columns: auto 1fr auto;
    }

    .condition-col-property,
    .condition-col-operator {
        width: 8rem;
    }

    .condition-col-value {
        width: 10rem;
        max-width: 20rem;
        flex-grow: 1;
    }

    .condition-col-subject {
        min-width: 10rem;
        /*flex: 1;*/
        flex-grow: 1;
    }

    .dynamic-subject-grid .align-with-input:not(:has(label)) {
        margin-top: var(--_label-height);
    }
</style>
